import { Csv } from '@/lib/csv'
import * as datefns from 'date-fns'
import type { Ref } from 'vue'

/**
 * Get the hash query params as a singleton to spare some resources
 */
const useQueryParamsStore = defineStore('search-params', () => {
  const params = useRouterQueryParams()
  return { params }
})

/**
 * Sync the given ref with a hash query parameter
 */
export function syncWithQueryParam<T = string>(
  name: string,
  value: Ref<T>,
  {
    serialize = value =>
      value === '' || value == null || value === false ? undefined : String(value),
    deserialize = value => value as T,
  }: {
    serialize?: (value: T) => string | undefined
    deserialize?: (serialized: string | undefined) => T
  } = {},
) {
  const { params } = useQueryParamsStore()
  const paramRef = toRef(params, name) as Ref<string>

  syncRef(paramRef, value, {
    direction: 'both',
    transform: {
      ltr: deserialize,
      rtl: serialize,
    },
    deep: true,
  } as any)
}

export type ParamTransform<T> = {
  serialize: (value: T) => string | undefined
  deserialize: (serialized: string) => T
}
export type BuiltInTransform = 'json' | 'string' | 'number' | 'boolean' | 'bigint' | 'date'

/**
 * Get a ref that is synced with a hash query parameter
 * The query parameter is omitted when it holds the default value
 *
 * Proper serialization/deserialization is auto-detected from the default value with built-in
 * support for primitives (except symbol and undefined), falling back to using JSON
 */
export function useParam<T, U extends string>(
  name: U,
  defaultValue: T,
  {
    transform = 'auto',
  }: {
    transform?: 'auto' | BuiltInTransform | ParamTransform<T>
  } = {},
) {
  const defaultTransforms = {
    json: {
      serialize: value => JSON.stringify(value),
      deserialize: value => JSON.parse(value) as T,
    } as ParamTransform<T>,
    string: {
      serialize: value => String(value),
      deserialize: value => value,
    } as ParamTransform<string>,
    number: {
      serialize: value => String(value),
      deserialize: value => Number(value),
    } as ParamTransform<number>,
    bigint: {
      serialize: value => String(value),
      deserialize: value => BigInt(value),
    } as ParamTransform<bigint>,
    boolean: {
      serialize: value => (value ? '1' : '0'),
      deserialize: value => value === '1',
    } as ParamTransform<boolean>,
    date: {
      serialize: value => value.toISOString(),
      deserialize: value => new Date(value),
    } as ParamTransform<Date>,
  }

  const { serialize, deserialize } = (
    typeof transform === 'string'
      ? defaultTransforms[
          transform === 'auto'
            ? (() => {
                if (typeof defaultValue === 'string') return 'string'
                if (typeof defaultValue === 'number') return 'number'
                if (typeof defaultValue === 'boolean') return 'boolean'
                return 'json'
              })()
            : transform
        ]
      : transform
  ) as ParamTransform<T>

  const serializedDefaultValue = serialize(defaultValue)
  const value = ref(defaultValue)

  const transforms = {
    serialize(value: T) {
      const serializedValue = serialize(value)
      if (serializedValue === serializedDefaultValue) return undefined
      return serializedValue
    },
    deserialize(value: string | undefined) {
      if (value === undefined) return defaultValue
      return deserialize(value)
    },
  }

  syncWithQueryParam<T>(name, value as Ref<T>, {
    ...transforms,
  })

  return {
    current: value,
    name,
    default: defaultValue,
    ...transforms,
  }
}

/**
 * A space-efficient way to store more complex data in a query parameter
 */
export function useCsvParam<T, U extends string>(
  name: U,
  defaultValue: T,
  {
    headers = false,
    flatten = value => (Array.isArray(defaultValue) ? value : [value]) as any,
    unflatten = value => (Array.isArray(defaultValue) ? value : value[0]) as T,
  }: {
    headers?: boolean | string[]
    flatten?: (value: T) => any[]
    unflatten?: (value: any[]) => T
  },
) {
  const csv = new Csv({
    delimiterChar: ':',
    quoteChar: '|',
    escapeChar: '|',
    newlineChar: ',',
  })

  return useParam(name, defaultValue, {
    transform: {
      serialize: value => {
        const flatValue = flatten(value)
        return csv.encode(flatValue, { headers })
      },
      deserialize: serialized => {
        const decodedValue = csv.decode(serialized, { headers })
        return unflatten(decodedValue)
      },
    },
  })
}

export function useDateParam(
  name: string,
  defaultValue: Date | undefined = undefined,
  aspect: 'date' | 'month' | 'year' = 'date',
) {
  let format = aspect === 'year' ? 'yyyy' : aspect === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd'

  return useParam(name, defaultValue, {
    transform: {
      serialize: value => {
        if (!value) return ''

        return datefns.format(value, format)
      },
      deserialize: serialized => {
        if (serialized.length === 0) return undefined
        return datefns.parse(serialized, format, new Date())
      },
    },
  })
}

export function useDateRangeParam(
  name: string,
  defaultValue: [lower: Date, upper: Date] | undefined = undefined,
  aspect: 'date' | 'month' | 'year' = 'date',
) {
  let format = aspect === 'year' ? 'yyyy' : aspect === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd'

  return useParam(name, defaultValue, {
    transform: {
      serialize: value => {
        if (!value) return ''

        return datefns.format(value[0], format) + ':' + datefns.format(value[1], format)
      },
      deserialize: serialized => {
        if (serialized.length === 0) return undefined
        const [lower, upper] = serialized.split(':')
        const reference = new Date()
        return [
          datefns.parse(lower, format, reference),
          datefns.parse(upper, format, reference),
        ] as [lower: Date, upper: Date]
      },
    },
  })
}
